/**
 * QL-416 — Cost/benefit component (portfolio pages, advisor + public).
 *
 * Reads the per-portfolio `cost_benefit` snapshot, which already embeds the
 * frozen existing side, and renders:
 *   - existing vs new metrics (expected return, volatility, product costs),
 *   - the one-time costs of the transition,
 *   - the QL-304 coverage note when part of the existing portfolio could not
 *     be analysed (unmapped or unclassified holdings),
 *   - the unmapped-holdings warning.
 * A portfolio that is not a followup shows "No existing portfolio" only.
 */

import type { CostBenefitSnapshot } from "@repo/shared/types";
import { Info } from "lucide-react";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { usePortfolioSnapshot } from "@/lib/hooks/use-portfolio-snapshot";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { CostBenefitConfig } from "@/lib/types/flow";
import type { PortfolioInfo } from "../types";
import { T } from "./cost-benefit.i18n";
import { MetricsComparisonTable } from "./metrics-comparison-table";
import { OneTimeCostsTable } from "./one-time-costs-table";
import { UnmatchedHoldingsWarning } from "./unmatched-holdings-warning";

type CostBenefitProps = {
  portfolioId: string | null;
  config: CostBenefitConfig;
  /** QL-311 — names the actual missing input in the empty state (same chain as the sibling widgets). */
  portfolioInfo: PortfolioInfo;
  /** The snapshot is only generated for one-time portfolios (QL-416 scope). */
  isOneTimePortfolio: boolean;
  /** Header wording per followup lineage — from the shared predicate in the renderer. */
  comparisonVariant?: "portfolio" | "savingsPlan";
  /** Completion engine reported the snapshot missing for this component on this portfolio. */
  hasValidationError?: boolean;
};

const placeholder = (text: string) => (
  <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">{text}</div>
);

export function CostBenefit({
  portfolioId,
  config,
  portfolioInfo,
  isOneTimePortfolio,
  comparisonVariant = "portfolio",
  hasValidationError = false,
}: CostBenefitProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  const snapshotQuery = usePortfolioSnapshot("costBenefit", portfolioId);
  const portfolio = usePortfolioSnapshot("portfolio", portfolioId);

  if (snapshotQuery.isLoading || portfolio.isLoading) return null;

  if (!isOneTimePortfolio) return placeholder(t.oneTimeOnly);

  if (!snapshotQuery.data) {
    if (!portfolio.data) {
      // QL-311 — name the actual missing input, walking the empty-state
      // contract order (custom → risk → amount → score). A custom portfolio
      // is driven by the builder, so neither input gates it.
      if (!portfolioInfo.isCustomPortfolio) {
        if (!portfolioInfo.hasRiskAssessment) return placeholder(t.completeRiskAssessment);
        if (!portfolioInfo.hasInvestmentAmount) return placeholder(t.enterInvestmentAmount);
        return placeholder(t.noPortfolioForScore(String(portfolioInfo.riskScore ?? "")));
      }
      return placeholder(t.noProducts);
    }
    // Prerequisites are met but the row is missing — the engine flagged it.
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissing}
        </div>
      );
    }
    return placeholder(t.notAvailable);
  }

  const snapshot = parseSnapshot(snapshotQuery.data);
  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  if (!snapshot.existing) {
    return (
      <VisualizationContainer config={config}>
        <p className="text-sm text-muted-foreground" data-testid="cost-benefit-no-existing">
          {t.noExistingPortfolio}
        </p>
      </VisualizationContainer>
    );
  }

  // The note reports the share the return/volatility rows are blended over
  // (the classified share; older rows carry only the mapped share). Rounded
  // first so the gate and the label agree.
  const analysedPct = Math.round(snapshot.existing.classifiedPct ?? snapshot.existing.coveragePct);
  const showCoverageNote = analysedPct < 100;

  return (
    <VisualizationContainer config={config}>
      <MetricsComparisonTable
        existing={snapshot.existing}
        proposed={snapshot.proposed}
        variant={comparisonVariant}
      />
      {snapshot.oneTimeCosts && (
        <OneTimeCostsTable costs={snapshot.oneTimeCosts} currency={snapshot.currency} />
      )}
      {showCoverageNote && (
        <div
          className="flex items-start gap-2 text-sm text-muted-foreground"
          data-testid="cost-benefit-coverage-note"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t.coverageNote(
              formatNumber(analysedPct, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            )}
          </span>
        </div>
      )}
      <UnmatchedHoldingsWarning holdings={snapshot.existing.unmatched} />
    </VisualizationContainer>
  );
}

function parseSnapshot(data: unknown): CostBenefitSnapshot | null {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as CostBenefitSnapshot;
    } catch {
      return null;
    }
  }
  if (data && typeof data === "object" && "proposed" in data) return data as CostBenefitSnapshot;
  return null;
}
